import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

export const ACCESS_TOKEN_KEY = 'homy_token';
export const REFRESH_TOKEN_KEY = 'homy_refresh_token';
export const USER_KEY = 'homy_user';

// In-memory fallback cache (used when localStorage is unavailable, disabled, or throws)
const memoryStorage = new Map();

function isNativePlatform() {
  try {
    return (
      typeof Capacitor !== 'undefined' &&
      typeof Capacitor.isNativePlatform === 'function' &&
      Capacitor.isNativePlatform()
    );
  } catch {
    return false;
  }
}

function isLocalStorageAvailable() {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return false;
  }
  try {
    const testKey = '__homy_storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

export function safeGetItem(key) {
  if (isLocalStorageAvailable()) {
    try {
      const val = window.localStorage.getItem(key);
      if (val !== null) return val;
    } catch {
      // Fall through to in-memory fallback
    }
  }
  return memoryStorage.has(key) ? memoryStorage.get(key) : null;
}

export function safeSetItem(key, value) {
  if (isLocalStorageAvailable()) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      memoryStorage.set(key, value);
    }
  } else {
    memoryStorage.set(key, value);
  }
  if (isNativePlatform()) {
    Preferences.set({ key, value }).catch((err) => {
      console.warn(`[tokenStorage] Native Preferences.set failed for ${key}:`, err);
    });
  }
}

export function safeRemoveItem(key) {
  memoryStorage.delete(key);
  if (isLocalStorageAvailable()) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore
    }
  }
  if (isNativePlatform()) {
    Preferences.remove({ key }).catch((err) => {
      console.warn(`[tokenStorage] Native Preferences.remove failed for ${key}:`, err);
    });
  }
}

export const tokenStorage = {
  getAccessToken() {
    return safeGetItem(ACCESS_TOKEN_KEY);
  },
  setAccessToken(token) {
    if (token) {
      safeSetItem(ACCESS_TOKEN_KEY, token);
    } else {
      safeRemoveItem(ACCESS_TOKEN_KEY);
    }
  },
  getRefreshToken() {
    return safeGetItem(REFRESH_TOKEN_KEY);
  },
  setRefreshToken(token) {
    if (token) {
      safeSetItem(REFRESH_TOKEN_KEY, token);
    } else {
      safeRemoveItem(REFRESH_TOKEN_KEY);
    }
  },
  getUser() {
    const raw = safeGetItem(USER_KEY);
    if (!raw) return null;
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return null;
    }
  },
  setUser(user) {
    if (user) {
      const serialized = typeof user === 'string' ? user : JSON.stringify(user);
      safeSetItem(USER_KEY, serialized);
    } else {
      safeRemoveItem(USER_KEY);
    }
  },
  clearTokens() {
    safeRemoveItem(ACCESS_TOKEN_KEY);
    safeRemoveItem(REFRESH_TOKEN_KEY);
    safeRemoveItem(USER_KEY);
  },
};

export const getAccessToken = () => tokenStorage.getAccessToken();
export const setAccessToken = (token) => tokenStorage.setAccessToken(token);
export const getRefreshToken = () => tokenStorage.getRefreshToken();
export const setRefreshToken = (token) => tokenStorage.setRefreshToken(token);
export const getUser = () => tokenStorage.getUser();
export const setUser = (user) => tokenStorage.setUser(user);
export const clearTokens = () => tokenStorage.clearTokens();

export default tokenStorage;
